/* eslint-disable max-len */
import { classnames } from 'helpers/utils';
import { removeFileExtension } from 'helpers/file-utils';
import { useContext, useEffect, useRef } from 'react';
import { SelfRecordingStatus, VideoStatus } from 'common/enums';
import { useFileUpload } from 'hooks/use-file-upload';
import { SelfRecordingContext } from 'context/self-recording-context';
import { selfRecordingActions } from 'context/self-recording-context/action-types';
import { AllVideoProps } from 'networking/types/all-video-props';
import styles from './video-row.module.scss';

type VideoRowProps = {
  vaultId: number;
  questionSelected?: ClipType;
  video: AllVideoProps;
  notifyVideoStatusChange: (id: number, newStatus: string) => void;
};

const VideoRow: React.FC<VideoRowProps> = ({
  video,
  notifyVideoStatusChange,
}) => {
  const {
    state: { selectedQuestion },
    dispatch,
  } = useContext(SelfRecordingContext);
  const { uploadS3File, response, error, errorWithId } = useFileUpload();
  // Guards against a duplicate upload of the same file. React.StrictMode (dev)
  // double-invokes effects, and the status prop hasn't propagated yet on the
  // second run, so without this the file would upload — and be created — twice.
  const hasStartedUploadRef = useRef(false);

  const showFileName = () => (
    <span
      className={classnames(
        'text__body__regular__small__gray800',
        styles.videoName,
        {
          [styles.videoNameInProgress]: video.status === VideoStatus.inProgress,
        },
      )}
    >
      {video.file.name}
    </span>
  );

  const showContent = () => (
    <div key={video.id} className={styles.videoRow}>
      {showFileName()}
    </div>
  );

  useEffect(() => {
    if (video.status === VideoStatus.new && !hasStartedUploadRef.current) {
      hasStartedUploadRef.current = true;
      notifyVideoStatusChange(video.id, VideoStatus.inProgress);
      dispatch({
        type: selfRecordingActions.addUpload,
        addUpload: {
          type: video.contentType,
          data: video.file.name,
          isUploading: true,
          uploadId: video.uploadId,
        },
      });
      uploadS3File(video.file, video.uploadId);
    }
  }, [video]);

  useEffect(() => {
    if (response && response.requestId === video.uploadId) {
      notifyVideoStatusChange(video.id, VideoStatus.ready);
      dispatch({
        type: selfRecordingActions.newClip,
        videoToAppend: {
          requestId: video.uploadId,
          clip: selectedQuestion,
          videoId: response.id,
          videoUrl: response.url,
          status: SelfRecordingStatus.recorded,
          // Becomes the memory's name, so without the file extension.
          fileName: !selectedQuestion
            ? removeFileExtension(video.file.name)
            : undefined,
          uploadStartTime: video.uploadStartTime,
          videoSignedId: response.signedId,
          contentType: video.contentType,
        },
      });

      dispatch({ type: selfRecordingActions.setReadyForNextStep });
    }
  }, [response]);

  useEffect(() => {
    const hasError =
      error ||
      (errorWithId &&
        errorWithId.requestId === video.uploadId &&
        errorWithId.error);
    if (hasError) {
      notifyVideoStatusChange(video.id, VideoStatus.failed);
      dispatch({
        type: selfRecordingActions.updateUpload,
        updateUpload: {
          isUploading: false,
          type: video.contentType,
          data: video.file.name,
          error: true,
          uploadId: video.uploadId,
        },
      });
    }
  }, [error, errorWithId]);

  return showContent();
};

export { VideoRow };
